/**
 * useFormValidation Hook
 * Manages form validation strategy and error tracking
 */

import { useCallback, useMemo, useState } from 'react';
import { FormContainerConfig, IValidationStrategy } from '../types/index';
import { ValidationStrategyFactory } from '../strategies/ValidationStrategies';
import { useMasterDataStore } from '../stores/MasterDataStore';

export interface UseFormValidationReturn {
  strategy: IValidationStrategy;
  errors: Record<string, any[]>;
  validateAll: () => Promise<void>;
  clearErrors: () => void;
  hasErrors: boolean;
}

/**
 * Hook for managing form validation
 * Handles:
 * - Creating validation strategy from config
 * - Tracking validation errors
 * - Validating all forms
 */
export function useFormValidation(
  config: FormContainerConfig
): UseFormValidationReturn {
  const [errors, setErrors] = useState<Record<string, any[]>>({});
  const masterData = useMasterDataStore();

  // Create validation strategy based on config
  const strategy = useMemo(
    () => ValidationStrategyFactory.createStrategy(config.validationMode),
    [config.validationMode]
  );

  // Validate all forms
  const validateAll = useCallback(async () => {
    const allData = masterData.getData();
    const validationErrors = await strategy.validateBeforeSave(allData);

    if (validationErrors && validationErrors.length > 0) {
      // Extract per-form errors (format: { formID: [errors] })
      const errorsByForm: Record<string, any[]> = {};

      validationErrors.forEach((error: any) => {
        if (error.formID) {
          if (!errorsByForm[error.formID]) {
            errorsByForm[error.formID] = [];
          }
          errorsByForm[error.formID].push(error);
        }
      });

      setErrors(errorsByForm);
      strategy.onValidationError(validationErrors);
    } else {
      setErrors({});
    }
  }, [masterData, strategy]);

  // Clear errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Check if there are any errors
  const hasErrors = Object.values(errors).some((formErrors) => formErrors.length > 0);

  return {
    strategy,
    errors,
    validateAll,
    clearErrors,
    hasErrors,
  };
}
